// targetStore.ts — the selected read/write destination, shared by the panel, the editor and the level
// lists. Same observable-singleton shape as UnityBridge.ts.
//
// Backed by sessionStorage, which is per-tab by design: the choice survives page switches and reloads
// inside one tab, but a NEW tab starts unselected. localStorage would share the choice across every tab
// and outlive the session, which would quietly undo the point of making production a deliberate pick.
// Mirrors the backend's LevelTarget.ProductionId (Conveyor.ContentEditor/Drive/LevelTarget.cs). There is no
// shared type between the two projects, so this is the one place the client hardcodes it — everything else
// should import this constant instead of repeating the string literal.
export const PRODUCTION_TARGET_ID = "production";
const STORAGE_KEY = "conveyor.contentEditor.target";
class TargetStore {
    constructor() {
        this.selectedId = null;
        this.listeners = new Set();
        this.attentionListeners = new Set();
        this.select = (id) => {
            this.selectedId = id;
            try {
                if (id === null) {
                    window.sessionStorage.removeItem(STORAGE_KEY);
                }
                else {
                    window.sessionStorage.setItem(STORAGE_KEY, id);
                }
            }
            catch {
                // Storage unavailable — the in-memory choice still works for this page view.
            }
            this.listeners.forEach((fn) => fn());
        };
        // Drops a remembered target that no longer exists: the tab may have been open since before someone
        // deleted that dev environment, and writing to a missing folder would fail deep inside Drive instead
        // of here.
        this.reconcile = (available) => {
            if (this.selectedId !== null && !available.some((t) => t.id === this.selectedId)) {
                this.select(null);
            }
        };
        // Fired when a write was attempted with nothing selected: the selector pulses instead of the app
        // sending a request that the backend would reject anyway.
        this.requestAttention = () => {
            this.attentionListeners.forEach((fn) => fn());
        };
        this.subscribe = (fn) => {
            this.listeners.add(fn);
            return () => {
                this.listeners.delete(fn);
            };
        };
        this.subscribeAttention = (fn) => {
            this.attentionListeners.add(fn);
            return () => {
                this.attentionListeners.delete(fn);
            };
        };
        try {
            this.selectedId = window.sessionStorage.getItem(STORAGE_KEY);
        }
        catch {
            this.selectedId = null; // storage blocked (private mode) — fall back to in-memory only
        }
    }
    get id() {
        return this.selectedId;
    }
    get isSelected() {
        return this.selectedId !== null;
    }
}
export const targetStore = new TargetStore();
